import { createClient } from '@supabase/supabase-js';
import type { User } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storageKey: 'buildc-auth',
  },
});

/**
 * Returns the BuildC application role for a Supabase user.
 *
 * Priority: JWT top-level `role` claim (set by the custom_access_token_hook) →
 * fallback to `app_metadata.role` (set via updateUserById during signup).
 *
 * Supabase always sets role: "authenticated" as the default database role.
 * The JWT hook replaces this with the real BuildC role (e.g. "superadmin",
 * "builder_admin", "client"). If the hook is not enabled we fall back to
 * app_metadata which is set explicitly during signup.
 */
export function getEffectiveRole(user: User | null | undefined): string | undefined {
  if (!user) return undefined;
  const jwtRole = user.role;
  if (jwtRole && jwtRole !== 'authenticated') return jwtRole;
  return (user.app_metadata as Record<string, unknown>)?.role as string | undefined;
}

export async function getAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  const session = data.session;
  if (!session) return null;

  const expiresAtMs = session.expires_at ? session.expires_at * 1000 : 0;
  const refreshWindowMs = 60_000;

  if (expiresAtMs && expiresAtMs - Date.now() <= refreshWindowMs) {
    const { data: refreshed, error } = await supabase.auth.refreshSession();
    if (error || !refreshed.session) {
      await clearAuthSession();
      return null;
    }
    return refreshed.session.access_token;
  }

  return session.access_token;
}

export async function clearAuthSession(): Promise<void> {
  await supabase.auth.signOut();
}
